'use client';

import { Link, useNavigate } from 'react-router-dom';
import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  BellIcon,
  ChevronDownIcon,
  HomeIcon,
  DocumentTextIcon,
  FolderIcon,
  QuestionMarkCircleIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
  UserCircleIcon,
  LinkIcon,
  Squares2X2Icon,
  KeyIcon,
  CreditCardIcon,
  SparklesIcon,
  MapIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { useAssistantAction } from '../../contexts/AssistantActionContext';
import { getAvatarUrlWithFallback } from '../../utils/avatarUtils';
import { apiConfig } from '@/config/api';
import ProfileSwitcher from '../profile/ProfileSwitcher';
import { Profile } from '@/app/api/profiles';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function AppNavigation() {
  const { user, logout } = useAuth();
  const { triggerAssistant } = useAssistantAction();
  const navigate = useNavigate();

  const handleProfileSwitched = (_profile: Profile) => {
    // Refresh the page content after switching profiles
    navigate(window.location.pathname, { replace: true });
    window.location.reload();
  };

  const handleTriggerOnboarding = () => {
    triggerAssistant('ONBOARDING');
  };

  const handleNavigation = (href: string, e: React.MouseEvent, closeMenu?: () => void) => {
    e.preventDefault();
    if (window.location.pathname === href) {
      if (closeMenu) closeMenu();
      return;
    }
    document.dispatchEvent(new CustomEvent('routeChangeStart'));
    if (closeMenu) closeMenu();
    setTimeout(() => {
      navigate(href);
    }, 50);
  };

  const handleSignOut = async () => {
    try {
      await logout();
      // AuthContext.logout() handles redirect with ?reauth=true
      // No additional redirect needed here
    } catch (error) {
      console.error('Error signing out:', error);
      // Fallback: redirect with reauth param to prevent cookie loop
      window.location.href = `${apiConfig.wwwUrl}/signin/?reauth=true`;
    }
  };

  if (!user) {
    return null; // Don't render anything if there's no user
  }

  return (
    <nav className="bg-white shadow-sm relative z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            {/* Mobile menu button */}
            <div className="flex items-center md:hidden mr-2">
              <Menu as="div" className="relative">
                <Menu.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                  <span className="sr-only">Open main menu</span>
                  <div className="h-6 w-6 flex flex-col justify-center space-y-1.5">
                    <span className="block h-0.5 w-6 bg-current"></span>
                    <span className="block h-0.5 w-6 bg-current"></span>
                    <span className="block h-0.5 w-6 bg-current"></span>
                  </div>
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute left-0 mt-2 w-72 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 divide-y divide-gray-100">
                    {/* Profile Switcher - mobile */}
                    <ProfileSwitcher
                      variant="mobile"
                      onProfileSwitched={handleProfileSwitched}
                      onTriggerOnboarding={handleTriggerOnboarding}
                    />
                    <div className="py-1">
                      <Menu.Item>
                        {({ active, close }) => (
                          <Link
                            to="/dashboard"
                            onClick={(e) => handleNavigation('/dashboard', e, close)}
                            className={classNames(
                              active ? 'bg-gray-100' : '',
                              'group flex items-center px-4 py-2 text-sm text-gray-700'
                            )}
                          >
                            <HomeIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                            Dashboard
                          </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active, close }) => (
                          <Link
                            to="/articles"
                            onClick={(e) => handleNavigation('/articles', e, close)}
                            className={classNames(
                              active ? 'bg-gray-100' : '',
                              'group flex items-center px-4 py-2 text-sm text-gray-700'
                            )}
                          >
                            <DocumentTextIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                            Articles
                          </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active, close }) => (
                          <Link
                            to="/assets"
                            onClick={(e) => handleNavigation('/assets', e, close)}
                            className={classNames(
                              active ? 'bg-gray-100' : '',
                              'group flex items-center px-4 py-2 text-sm text-gray-700'
                            )}
                          >
                            <FolderIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                            Assets
                          </Link>
                        )}
                      </Menu.Item>
                    </div>
                    <div className="py-1">
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Settings
                      </div>
                      <Menu.Item>
                        {({ active, close }) => (
                          <Link
                            to="/user/settings"
                            onClick={(e) => handleNavigation('/user/settings', e, close)}
                            className={classNames(
                              active ? 'bg-gray-100' : '',
                              'group flex items-center px-4 py-2 text-sm text-gray-700'
                            )}
                          >
                            <Cog6ToothIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                            General Settings
                          </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active, close }) => (
                          <Link
                            to="/user/connections"
                            onClick={(e) => handleNavigation('/user/connections', e, close)}
                            className={classNames(
                              active ? 'bg-gray-100' : '',
                              'group flex items-center px-4 py-2 text-sm text-gray-700'
                            )}
                          >
                            <LinkIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                            Connections
                          </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active, close }) => (
                          <Link
                            to="/user/content-strategy"
                            onClick={(e) => handleNavigation('/user/content-strategy', e, close)}
                            className={classNames(
                              active ? 'bg-gray-100' : '',
                              'group flex items-center px-4 py-2 text-sm text-gray-700'
                            )}
                          >
                            <MapIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                            Content Strategy
                          </Link>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>

            <div className="flex-shrink-0">
              <Link to="/dashboard" className="flex items-center space-x-2">
                <img className="h-8 w-auto" src="/logo.svg" alt="Content Growth" />
                <span className="font-semibold text-lg text-gray-800 hidden sm:block">Content Growth</span>
              </Link>
            </div>
          </div>

          <div className="hidden md:flex items-center ml-10 space-x-4 lg:space-x-8">
            {/* Profile Switcher - desktop */}
            <ProfileSwitcher
              variant="nav"
              onProfileSwitched={handleProfileSwitched}
              onTriggerOnboarding={handleTriggerOnboarding}
            />

            <div className="w-px h-5 bg-gray-200" />

            <Link
              to="/dashboard"
              onClick={(e) => handleNavigation('/dashboard', e)}
              className="text-sm font-medium text-gray-900 hover:text-gray-700 flex items-center px-2 py-2 rounded-md hover:bg-gray-50"
            >
              <HomeIcon className="h-4 w-4 mr-2" />
              Dashboard
            </Link>
            <Link
              to="/articles"
              onClick={(e) => handleNavigation('/articles', e)}
              className="text-sm font-medium text-gray-900 hover:text-gray-700 flex items-center px-2 py-2 rounded-md hover:bg-gray-50"
            >
              <DocumentTextIcon className="h-4 w-4 mr-2" />
              Articles
            </Link>
            <Link
              to="/assets"
              onClick={(e) => handleNavigation('/assets', e)}
              className="text-sm font-medium text-gray-900 hover:text-gray-700 flex items-center px-2 py-2 rounded-md hover:bg-gray-50"
            >
              <FolderIcon className="h-4 w-4 mr-2" />
              Assets
            </Link>

            <Menu as="div" className="relative">
              <Menu.Button className="text-sm font-medium text-gray-900 hover:text-gray-700 flex items-center px-2 py-2 rounded-md hover:bg-gray-50 focus:outline-none">
                <Cog6ToothIcon className="h-4 w-4 mr-2" />
                <span className="hidden lg:inline">Settings</span>
                <ChevronDownIcon className="ml-1 h-3 w-3" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute left-0 mt-2 w-56 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <div className="py-1">
                    <Menu.Item>
                      {({ active, close }) => (
                        <Link
                          to="/user/settings"
                          onClick={(e) => handleNavigation('/user/settings', e, close)}
                          className={classNames(
                            active ? 'bg-gray-100' : '',
                            'flex items-center px-4 py-2 text-sm text-gray-700'
                          )}
                        >
                          <Cog6ToothIcon className="h-4 w-4 mr-2 text-gray-400" />
                          General Settings
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active, close }) => (
                        <Link
                          to="/user/connections"
                          onClick={(e) => handleNavigation('/user/connections', e, close)}
                          className={classNames(
                            active ? 'bg-gray-100' : '',
                            'flex items-center px-4 py-2 text-sm text-gray-700'
                          )}
                        >
                          <LinkIcon className="h-4 w-4 mr-2 text-gray-400" />
                          Connections
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active, close }) => (
                        <Link
                          to="/user/content-strategy"
                          onClick={(e) => handleNavigation('/user/content-strategy', e, close)}
                          className={classNames(
                            active ? 'bg-gray-100' : '',
                            'flex items-center px-4 py-2 text-sm text-gray-700'
                          )}
                        >
                          <MapIcon className="h-4 w-4 mr-2 text-gray-400" />
                          Content Strategy
                        </Link>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>

          <div className="flex items-center space-x-1 sm:space-x-4">
            {/* Mobile Assistant Trigger */}
            <button
              onClick={() => triggerAssistant()}
              className="md:hidden p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-full"
              aria-label="Open Assistant"
            >
              <SparklesIcon className="h-6 w-6 text-blue-600" />
            </button>

            <Menu as="div" className="relative">
              {user.unread && <Menu.Button className="p-2 text-gray-400 hover:text-gray-500 relative">
                <BellIcon className="h-6 w-6" />
                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white"></span>
              </Menu.Button>}
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-2 w-80 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <div className="py-2">
                    <div className="px-4 py-2 text-sm text-gray-500 border-b">
                      Notifications
                    </div>
                    <div className="px-4 py-3 text-sm text-gray-500">
                      No new notifications
                    </div>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>

            {!user.onboarded && (
              <div className="relative" title="Show onboarding guide">
                <button
                  onClick={() => triggerAssistant()}
                  className="p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-full"
                >
                  <QuestionMarkCircleIcon className="h-6 w-6" />
                </button>
              </div>
            )}

            <Menu as="div" className="relative">
              <Menu.Button className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                <img
                  className="h-8 w-8 rounded-full"
                  src={getAvatarUrlWithFallback(user.photoURL)}
                  alt={user.displayName || user.email || 'User'}
                  width={32}
                  height={32}
                />
                <ChevronDownIcon className="ml-2 h-4 w-4 text-gray-400 hidden sm:block" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <div className="py-1">
                    {user.is_system_admin && (
                      <>
                        <Menu.Item>
                          {({ active }) => (
                            <a
                              href="/admin-tools"
                              target="_blank"
                              rel="noopener noreferrer"
                              className={classNames(
                                active ? 'bg-gray-100' : '',
                                'flex items-center px-4 py-2 text-sm font-medium text-gray-800'
                              )}
                            >
                              <ShieldCheckIcon className="h-5 w-5 mr-2 text-blue-600" aria-hidden="true" />
                              <span>Admin Portal</span>
                            </a>
                          )}
                        </Menu.Item>
                        <div className="my-1 border-t border-gray-100" />
                      </>
                    )}
                    <Menu.Item>
                      {({ active, close }) => (
                        <Link
                          to="/user/profile"
                          onClick={(e) => handleNavigation('/user/profile', e, close)}
                          className={classNames(
                            active ? 'bg-gray-100' : '',
                            'block px-4 py-2 text-sm text-gray-700'
                          )}
                        >
                          Profile
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active, close }) => (
                        <Link
                          to="/billing"
                          onClick={(e) => handleNavigation('/billing', e, close)}
                          className={classNames(
                            active ? 'bg-gray-100' : '',
                            'block px-4 py-2 text-sm text-gray-700'
                          )}
                        >
                          Billing
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleSignOut}
                          className={classNames(
                            active ? 'bg-gray-100' : '',
                            'block w-full text-left px-4 py-2 text-sm text-gray-700'
                          )}
                        >
                          Sign out
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div >
    </nav >
  );
}
